import {
  PERCEPTION_MODEL_TRANSLATION,
  PERCEPTION_MODEL_TRANSLATION_NEW,
} from 'constants/routePaths';

import * as React from 'react';

import { useMutation, useQuery } from '@apollo/client';
import DeletePerceptionModelProjectTranslation from '@graphql/mutations/DeletePerceptionModelProjectTranslation';
import {
  AllProjectTranslationsQuery,
  type TranslationListData,
  type TranslationRow,
} from '@graphql/queries/PerceptionModelQueries';
import { Add, Delete } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  Typography,
} from '@mui/material';
import { useHistory } from 'react-router-dom';

import { describeDemographic } from './demographic';
import PerceptionModelError from './PerceptionModelError';

/**
 * The translations hanging off one norm set.
 *
 * Parent-child: the norm set stays immutable, and its translations are separate
 * rows that can come and go. Deleting the norm set cascades to them; deleting a
 * translation leaves the norm set alone.
 *
 * Adding one is a route, not a panel opened here, so that the sidebar's "Add
 * translation" and this button lead to the same place. Two entry points onto
 * one builder; not two builders.
 */

type Props = { projectId: number };

const newTranslationPath = (projectId: number) =>
  PERCEPTION_MODEL_TRANSLATION_NEW.replace(':normSetId', String(projectId));

const TranslationSection: React.FC<Props> = ({ projectId }) => {
  const history = useHistory();
  // Deleting is irreversible and the control is one click from a list, which
  // is exactly where a misfire happens.
  const [confirming, setConfirming] = React.useState<TranslationRow | null>(
    null,
  );

  const { data, loading, error } = useQuery(AllProjectTranslationsQuery, {
    variables: { projectId },
  });
  const translations: TranslationRow[] =
    data?.allPerceptionModelProjectTranslations?.nodes ?? [];

  const [remove, { error: deleteError }] = useMutation(
    DeletePerceptionModelProjectTranslation,
    {
      update: (cache, _r, { variables }) => {
        const existing = cache.readQuery<TranslationListData>({
          query: AllProjectTranslationsQuery,
          variables: { projectId },
        });
        if (!existing?.allPerceptionModelProjectTranslations) return;
        cache.writeQuery({
          query: AllProjectTranslationsQuery,
          variables: { projectId },
          data: {
            allPerceptionModelProjectTranslations: {
              ...existing.allPerceptionModelProjectTranslations,
              nodes:
                existing.allPerceptionModelProjectTranslations.nodes.filter(
                  (n) => n.id !== variables?.id,
                ),
            },
          },
        });
      },
      // Without this the execute function rejects unhandled and the row simply
      // stays put, with nothing on screen saying why.
      onError: () => {},
    },
  );

  return (
    <Box sx={{ mt: 2 }}>
      <Divider sx={{ mb: 1 }} />

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="subtitle1">Translations</Typography>
        {/* Nothing to add to when the table is not there: the builder would
            render fine and fail on save. */}
        {!error && (
          <Button
            size="small"
            startIcon={<Add />}
            onClick={() => history.push(newTranslationPath(projectId))}
            sx={{ textTransform: 'none' }}
          >
            Add translation
          </Button>
        )}
      </Box>

      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
        How this norm set reads to a particular audience. Rendered against the
        model this norm set was built on, not the current one.
      </Typography>

      {/* "None yet" and "the table is missing" need different actions, and
          reporting the second as the first sends people to a builder that
          cannot save. */}
      {error && (
        <Box sx={{ mt: 1 }}>
          <PerceptionModelError error={error} />
        </Box>
      )}

      {deleteError && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {deleteError.message}
        </Alert>
      )}

      {!loading && !error && translations.length === 0 && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            No translations yet — add one to read this norm set as a particular
            audience.
          </Typography>
          <Button
            size="small"
            startIcon={<Add />}
            onClick={() => history.push(newTranslationPath(projectId))}
            sx={{ textTransform: 'none', mt: 0.5 }}
          >
            Add the first translation
          </Button>
        </Box>
      )}

      {translations.map((t) => (
        <Box
          key={t.id}
          sx={{
            display: 'flex',
            alignItems: 'center',
            py: 1,
            borderBottom: '1px solid rgba(0,0,0,0.08)',
          }}
        >
          <Box
            role="button"
            tabIndex={0}
            onClick={() =>
              history.push(
                PERCEPTION_MODEL_TRANSLATION.replace(
                  ':normSetId',
                  String(projectId),
                ).replace(':translationId', String(t.id)),
              )
            }
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                history.push(
                  PERCEPTION_MODEL_TRANSLATION.replace(
                    ':normSetId',
                    String(projectId),
                  ).replace(':translationId', String(t.id)),
                );
              }
            }}
            sx={{ flexGrow: 1, cursor: 'pointer' }}
          >
            <Typography variant="body2">{t.name}</Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {describeDemographic(t.demographic)} · {t.products?.length ?? 0}{' '}
              products
            </Typography>
          </Box>
          <IconButton
            size="small"
            aria-label={`Delete ${t.name}`}
            onClick={() => setConfirming(t)}
          >
            <Delete fontSize="small" />
          </IconButton>
        </Box>
      ))}
      <Dialog open={!!confirming} onClose={() => setConfirming(null)}>
        <DialogTitle>Delete “{confirming?.name}”?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This removes the audience definition and cannot be undone. The norm
            set and its products are untouched — only this way of reading them
            goes.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirming(null)}>Cancel</Button>
          <Button
            color="error"
            onClick={() => {
              if (confirming) remove({ variables: { id: confirming.id } });
              setConfirming(null);
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TranslationSection;
